import { useEffect } from 'react';

import { useGetAsignmentFieldsQuery, usePostAssignmentMutation } from 'services';
import { Form } from 'antd';
import { useDispatch } from 'react-redux';
import { fetchError } from 'slices/commonSlice';
import { useIntl } from 'react-intl';
import { openNotification } from 'utils';

import MediaField from '../MediaField';

import AsignItem from './AsignItem';

/**
 * @description Telif sonrası "Madde Atama" adımı: madde dosyaları + redaksiyon adımlarına kullanıcı atama formu.
 *
 * @param {string} props.offerURI - `ArticleOffer` IRI'si; atama isteğinde gönderilir.
 * @param {number} props.offerID - `ArticleOffer` id'si.
 * @param {number} props.type - Atama alanı tipi (0: Ansiklopedi Birimi, 1: Yayın Müdürlüğü).
 * @param {boolean} props.disabledAsignment - Süreç başladıysa atamalar kilitlenir.
 * @param {Array} [props.afterCopyright] - Mevcut atamalar (`GET /assignments`).
 * @param {React.ReactNode} [props.children=null] - Madde dosyalarının altına gömülecek içerik (medya teklifi tablosu).
 */
const ContentAsignment = ({ offerURI, offerID, type, disabledAsignment, afterCopyright, children = null }) => {
  const [postAssignment] = usePostAssignmentMutation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const { data: assignments } = useGetAsignmentFieldsQuery({
    type: type,
    articleOffer: offerID,
  });

  const handleIsEditable = (status) => {
    return afterCopyright?.some((item) => item.field.status === status);
  };

  useEffect(() => {
    if (afterCopyright) {
      form.setFieldsValue({
        assignments: afterCopyright.reduce((acc, item) => {
          acc[item.field.id] = {
            field: item['@id'],
            user: item.user?.id,
            id: item['@id'],
          };
          return acc;
        }, {}),
      });
    }
  }, [afterCopyright, form]);

  const handleNewAssignment = async ({ data }) => {
    const { error } = await postAssignment({
      data,
    });
    if (error) {
      return dispatch(
        fetchError(
          formatMessage(
            {
              id: 'article.form.fetchError',
            },
            { error: error.status }
          )
        )
      );
    }
    openNotification({
      message: formatMessage({
        id: 'appModule.actionSuccessfully',
      }),
    });
  };
  const handleValuesChange = async (changedValues) => {
    if (changedValues?.assignments) {
      const promises = Object.entries(changedValues.assignments).map(async ([index, value]) => {
        if (value.user) {
          const field = form.getFieldValue(['assignments', index, 'field']);
          const id = form.getFieldValue(['assignments', index, 'id']);

          await handleNewAssignment({
            data: {
              ...(id && { id }),
              articleOffer: offerURI,
              field,
              user: `/api/users/${value.user}`,
            },
          });
        }
      });

      await Promise.all(promises);
    }
  };

  return (
    <div>
      <h3>Madde Atama</h3>
      <MediaField
        offerID={offerID}
        isProcessContinue={false}
        isMediaEditable={false}
      >
        {children}
      </MediaField>
      <Form
        form={form}
        className="gx-mt-5"
        onValuesChange={handleValuesChange}
      >
        {assignments?.map((step) => (
          <AsignItem
            key={step.id}
            field={step['@id']}
            index={step.id}
            label={step.roleText}
            disabled={disabledAsignment}
            status={step.status}
            // edit butonu sadece karar verilmemis asamalarda gosterilecek
            // karar verilmis  asamalarda her hangi bir yeniden atama yapilamayacak
            showEditButton={step.assignments?.length > 0 && step.assignments[0]?.opinion !== 'approved'}
            role={step.role}
            handleIsEditable={handleIsEditable}
          />
        ))}
      </Form>
    </div>
  );
};

export default ContentAsignment;
